// Form1Sections — FORM 1 "PENDAFTARAN & SCREENING AWAL" (BRS-REC-WNA-001)
// sections beyond the original basic-info form: extra personal data, work
// readiness, education, experience, Islamic values & SANTRI screening,
// grooming, health and emergency contact + declaration.
//
// Every field name is prefixed `f1_` — Apply.jsx packs all f1_* values
// into applicants.form_data (jsonb) on submit. Plugged into the SAME
// react-hook-form instance as ApplicationForm (register/watch passed in).
//
// Labels come from apply.f1.* in the locale files (id/en/ar/ms) — inline
// defaults are Indonesian only as a last-resort fallback. The stored VALUES
// stay Indonesian ("Ya"/"Tidak"/"Bisa"/"Belum Bisa"/"Islam") whatever the
// applicant's language: HR reads form_data raw in the admin panel.

import { useTranslation } from 'react-i18next';
import { Field } from '../ui';
import FormSection from './FormSection';
import ResumeUpload from './ResumeUpload';

// The six religions recognised by the Indonesian state, Islam first (it is
// the relevant one for the SANTRI screening below), then "Lainnya".
// [stored value, locale key, Indonesian fallback] — the value is what lands
// in form_data and what HR reads, so it stays Indonesian in every language.
const RELIGIONS = [
  ['Islam', 'islam', 'Islam'],
  ['Kristen', 'christian', 'Kristen (Protestan)'],
  ['Katolik', 'catholic', 'Katolik'],
  ['Hindu', 'hindu', 'Hindu'],
  ['Buddha', 'buddhist', 'Buddha'],
  ['Konghucu', 'confucian', 'Konghucu'],
  ['Lainnya', 'otherReligion', 'Lainnya'],
];

// Stored values stay Indonesian, like every other FORM 1 answer, because HR
// reads form_data raw in the admin panel.
// [stored value, locale key, Indonesian fallback]
const EDU_LEVELS = [
  ['SD', 'eduSd', 'SD'],
  ['SMP', 'eduSmp', 'SMP'],
  ['SMA/SMK', 'eduSma', 'SMA/SMK'],
  ['D1/D3/S1', 'eduUni', 'D1/D3/S1'],
];

// pair: 'yn' → Ya/Tidak, 'can' → Bisa/Belum Bisa. noFirst puts the negative
// option first so the "safe" answer isn't the default-looking one.
function YN({ id, register, pair = 'yn', noFirst = false }) {
  const { t } = useTranslation();
  const [yes, no] = pair === 'can'
    ? [['Bisa', t('apply.f1.can', 'Bisa')], ['Belum Bisa', t('apply.f1.cannot', 'Belum Bisa')]]
    : [['Ya', t('apply.f1.yes', 'Ya')], ['Tidak', t('apply.f1.no', 'Tidak')]];
  const opts = noFirst ? [no, yes] : [yes, no];
  return (
    <select id={id} {...register(id)} className="input-field">
      <option value="">-</option>
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

export default function Form1Sections({ register, watch, errors, onQuranVideo }) {
  const { t } = useTranslation();
  const gender = watch('gender');
  const fnb = watch('f1_fnb');
  const health = watch('f1_h_condition');
  const quran = watch('f1_s_quran');
  const eduLevel = watch('f1_edu_level');
  const noMajor = eduLevel === 'SD' || eduLevel === 'SMP';

  const h3 = 'text-lg font-medium text-gray-900 mb-4';
  const h4 = 'text-xs font-semibold uppercase tracking-wide text-gray-500';
  const grid2 = 'grid grid-cols-1 md:grid-cols-2 gap-4';

  return (
    <>
      {/* == Data diri tambahan ================================== */}
      <FormSection
        index={4}
        icon="idCard"
        title={t('apply.f1.extraPersonal', 'Data Diri Tambahan')}
      >
        <div className="space-y-6">
          {/* Identitas */}
          <div className={grid2}>
            <Field id="f1_nickname" label={t('apply.f1.nickname', 'Nama Panggilan')}>
              <input id="f1_nickname" type="text" {...register('f1_nickname')} className="input-field" />
            </Field>
            <Field id="f1_nik" label={t('apply.f1.nik', 'NIK (No. KTP)')} error={errors.f1_nik?.message}>
              <input id="f1_nik" type="text" inputMode="numeric" maxLength={16}
                {...register('f1_nik', { pattern: { value: /^\d{16}$/, message: t('apply.f1.nikInvalid', 'NIK harus 16 digit angka') } })}
                className="input-field" />
            </Field>
            <Field id="f1_birthPlace" label={t('apply.f1.birthPlace', 'Tempat Lahir')}>
              <input id="f1_birthPlace" type="text" {...register('f1_birthPlace')} className="input-field" />
            </Field>
            <Field id="f1_religion" label={t('apply.f1.religion', 'Agama')}>
              <select id="f1_religion" {...register('f1_religion')} className="input-field">
                <option value="">-</option>
                {RELIGIONS.map(([value, key, fallback]) => (
                  <option key={value} value={value}>{t(`apply.f1.${key}`, fallback)}</option>
                ))}
              </select>
            </Field>
          </div>

          {/* Data fisik — kept in its own titled group. Sitting beside "Jumlah
              Anak" in the old flat grid, these read as the child's height and
              weight rather than the applicant's. */}
          <div>
            <div className="mb-3">
              <h4 className={h4}>{t('apply.f1.physical', 'Data Fisik Anda')}</h4>
              <p className="mt-1 text-sm text-gray-500">
                {t('apply.f1.physicalNote', 'Digunakan untuk ukuran seragam.')}
              </p>
            </div>
            <div className={grid2}>
              <Field id="f1_height" label={t('apply.f1.height', 'Tinggi (cm)')}>
                <input id="f1_height" type="number" min="0" {...register('f1_height')} className="input-field" />
              </Field>
              <Field id="f1_weight" label={t('apply.f1.weight', 'Berat (kg)')}>
                <input id="f1_weight" type="number" min="0" {...register('f1_weight')} className="input-field" />
              </Field>
            </div>
          </div>

          {/* Keluarga */}
          <div>
            <h4 className={`${h4} mb-3`}>{t('apply.f1.family', 'Keluarga')}</h4>
            <div className={grid2}>
              <Field id="f1_children" label={t('apply.f1.children', 'Jumlah Anak')}
                hint={t('apply.f1.childrenHint', 'Isi 0 jika belum memiliki anak.')}>
                <input id="f1_children" type="number" min="0" {...register('f1_children')} className="input-field" />
              </Field>
            </div>
          </div>

          {/* Alamat */}
          <div>
            <h4 className={`${h4} mb-3`}>{t('apply.f1.addressGroup', 'Alamat sesuai KTP')}</h4>
            <div className={grid2}>
              <Field id="f1_ktpAddress" label={t('apply.f1.ktpStreet', 'Alamat')} className="md:col-span-2">
                <textarea id="f1_ktpAddress" rows={2} {...register('f1_ktpAddress')} className="input-field" />
              </Field>
              <Field id="f1_kecamatan" label={t('apply.f1.kecamatan', 'Kecamatan')}>
                <input id="f1_kecamatan" type="text" {...register('f1_kecamatan')} className="input-field" />
              </Field>
              <Field id="f1_kabupaten" label={t('apply.f1.kabupaten', 'Kabupaten/Kota')}>
                <input id="f1_kabupaten" type="text" {...register('f1_kabupaten')} className="input-field" />
              </Field>
            </div>
          </div>
        </div>
      </FormSection>

      {/* == Kesiapan kerja ====================================== */}
      <FormSection
        index={5}
        icon="briefcase"
        title={t('apply.f1.readiness', 'Posisi dan Kesiapan Kerja')}
      >
        <div className="space-y-3">
          {[
            ['f1_k_outlet', t('apply.f1.kOutlet', 'Bersedia ditempatkan di outlet lain')],
            ['f1_k_shift', t('apply.f1.kShift', 'Bersedia bekerja shift')],
            ['f1_k_schedule', t('apply.f1.kSchedule', 'Bersedia bekerja sesuai jadwal perusahaan')],
            ['f1_k_city', t('apply.f1.kCity', 'Bersedia ditempatkan di dalam/luar kota sesuai kebutuhan')],
            ['f1_k_sop', t('apply.f1.kSop', 'Bersedia menjalankan SOP sesuai jabatan')],
            ['f1_k_training', t('apply.f1.kTraining', 'Bersedia mengikuti training/onboarding')],
          ].map(([id, label]) => (
            <div key={id} className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 px-4 py-2.5">
              <label htmlFor={id} className="text-gray-700 text-sm">{label}</label>
              <div className="w-28 shrink-0"><YN id={id} register={register} /></div>
            </div>
          ))}
        </div>
      </FormSection>

      {/* == Pendidikan ========================================== */}
      {/* Last education only, not the full ladder. Asking every applicant to
          fill SD, SMP, SMA and D1 was twelve fields to learn one thing:
          how far they got. */}
      <FormSection
        index={6}
        icon="book"
        title={t('apply.f1.education', 'Pendidikan Terakhir')}
      >
        <div className={grid2}>
          <Field id="f1_edu_level" label={t('apply.f1.eduLevel', 'Jenjang pendidikan terakhir')}>
            <select id="f1_edu_level" {...register('f1_edu_level')} className="input-field">
              <option value="">-</option>
              {EDU_LEVELS.map(([value, key, fallback]) => (
                <option key={value} value={value}>{t(`apply.f1.${key}`, fallback)}</option>
              ))}
            </select>
          </Field>
          <Field id="f1_edu_school" label={t('apply.f1.schoolName', 'Nama sekolah')}>
            <input id="f1_edu_school" type="text" {...register('f1_edu_school')} className="input-field" />
          </Field>
          <Field
            id="f1_edu_major"
            label={t('apply.f1.major', 'Jurusan')}
            hint={noMajor ? t('apply.f1.majorNa', 'Tidak berlaku untuk jenjang ini.') : undefined}
          >
            {/* SD and SMP have no jurusan, so the field is disabled rather
                than left there inviting an answer that does not exist. */}
            <input
              id="f1_edu_major"
              type="text"
              {...register('f1_edu_major')}
              className="input-field"
              disabled={noMajor}
            />
          </Field>
          <Field id="f1_edu_year" label={t('apply.f1.gradYear', 'Tahun lulus')}>
            <input id="f1_edu_year" type="text" inputMode="numeric" maxLength={4}
              {...register('f1_edu_year')} className="input-field" />
          </Field>
          <Field id="f1_skills" label={t('apply.f1.skills', 'Keahlian/Sertifikat')} className="md:col-span-2">
            <input id="f1_skills" type="text" {...register('f1_skills')} className="input-field" />
          </Field>
        </div>
      </FormSection>

      {/* == Pengalaman kerja ==================================== */}
      <FormSection
        index={7}
        icon="clipboard"
        title={t('apply.f1.experience', 'Pengalaman Kerja')}
      >
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-lg border border-gray-200 p-3">
              <div className={`${h4} mb-2`}>{t('apply.f1.expRow', 'Pengalaman {{n}}', { n: i })}</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  ['company', t('apply.f1.company', 'Perusahaan')],
                  ['role', t('apply.f1.role', 'Jabatan')],
                  ['duration', t('apply.f1.duration', 'Lama bekerja')],
                  ['reason', t('apply.f1.leaveReason', 'Alasan berhenti')],
                ].map(([field, label]) => (
                  <input key={field} type="text" placeholder={label}
                    aria-label={`${t('apply.f1.expRow', 'Pengalaman {{n}}', { n: i })} — ${label}`}
                    {...register(`f1_exp${i}_${field}`)} className="input-field" />
                ))}
              </div>
            </div>
          ))}
          <div className={grid2}>
            <Field id="f1_fnb" label={t('apply.f1.fnb', 'Pernah bekerja di bidang F&B/Restaurant?')}>
              <YN id="f1_fnb" register={register} noFirst />
            </Field>
            {fnb === 'Ya' && (
              <Field id="f1_fnb_role" label={t('apply.f1.fnbRole', 'Sebagai apa?')}>
                <input id="f1_fnb_role" type="text" {...register('f1_fnb_role')} className="input-field" />
              </Field>
            )}
          </div>
        </div>
      </FormSection>

      {/* == Screening Nilai Islam & SANTRI ====================== */}
      <FormSection
        index={8}
        icon="moon"
        title={t('apply.f1.santri', 'Screening Nilai Islam & SANTRI')}
      >
        <p className="text-sm text-gray-500 mb-3">
          {t('apply.f1.santriDesc', 'WN Ampera memiliki budaya kerja berdasarkan nilai-nilai Islam dan Budaya SANTRI (Sopan Santun, Amanah, Nyaman, Tawadhu, Rajin-Rapih, Istiqomah).')}
        </p>
        <div className="space-y-3">
          {[
            ['f1_s_islam', t('apply.f1.sIslam', 'Beragama Islam'), null],
            ['f1_s_quran', t('apply.f1.sQuran', "Dapat membaca Al-Qur'an/mengaji"), 'can'],
            ['f1_s_values', t('apply.f1.sValues', 'Bersedia menjalankan nilai-nilai Islam sebagai tata perilaku kerja WN Ampera'), null],
            ['f1_s_santri', t('apply.f1.sSantri', 'Bersedia menjalankan Budaya SANTRI'), null],
            ['f1_s_sholat', t('apply.f1.sSholat', 'Bersedia menjalankan ibadah Sholat Wajib 5 Waktu dan pembinaan keagamaan yang ditetapkan perusahaan'), null],
            ['f1_s_pergaulan', t('apply.f1.sPergaulan', 'Bersedia mematuhi tata pergaulan dan akhlak yang ditetapkan perusahaan'), null],
            ['f1_s_sop', t('apply.f1.sSop', 'Bersedia mematuhi SOP dan tata tertib perusahaan'), null],
          ].map(([id, label, pair]) => (
            <div key={id} className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 px-4 py-2.5">
              <label htmlFor={id} className="text-gray-700 text-sm">{label}</label>
              <div className="w-32 shrink-0">
                <YN id={id} register={register} pair={pair || 'yn'} />
              </div>
            </div>
          ))}
          {quran === 'Bisa' && (
            <div className="rounded-lg border border-gray-200 px-4 py-3">
              <ResumeUpload
                label={t('apply.f1.quranVideo', 'Upload Video Mengaji (opsional, maks ±50MB)')}
                accept="video/*"
                maxSizeMB={50}
                onChange={onQuranVideo}
              />
            </div>
          )}
        </div>
      </FormSection>

      {/* == Grooming ============================================ */}
      <FormSection
        index={9}
        icon="mirror"
        title={t('apply.f1.grooming', 'Screening Penampilan/Grooming')}
      >
        <div className="space-y-3">
          {[
            ['f1_g_tattoo', t('apply.f1.gTattoo', 'Memiliki tato/bekas tato'), true],
            ['f1_g_piercing', gender === 'female'
              ? t('apply.f1.gPiercingF', 'Memiliki tindik di hidung/bibir')
              : t('apply.f1.gPiercingM', 'Memiliki tindik'), true],
            ['f1_g_standard', t('apply.f1.gStandard', 'Bersedia mengikuti standar grooming WN Ampera'), false],
            ...(gender === 'female'
              ? [['f1_g_hijab', t('apply.f1.gHijab', 'Bersedia menggunakan kerudung sesuai ketentuan'), false]]
              : []),
          ].map(([id, label, noFirst]) => (
            <div key={id} className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 px-4 py-2.5">
              <label htmlFor={id} className="text-gray-700 text-sm">{label}</label>
              <div className="w-28 shrink-0"><YN id={id} register={register} noFirst={noFirst} /></div>
            </div>
          ))}
        </div>
      </FormSection>

      {/* == Kesehatan =========================================== */}
      <FormSection
        index={10}
        icon="heartPulse"
        title={t('apply.f1.health', 'Screening Kondisi Kesehatan')}
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 px-4 py-2.5">
            <label htmlFor="f1_h_condition" className="text-gray-700 text-sm">
              {t('apply.f1.hCondition', 'Apakah terdapat kondisi kesehatan yang dapat menghambat pekerjaan yang dilamar?')}
            </label>
            <div className="w-28 shrink-0"><YN id="f1_h_condition" register={register} noFirst /></div>
          </div>
          {health === 'Ya' && (
            <Field id="f1_h_condition_desc" label={t('apply.f1.hConditionDesc', 'Jelaskan')}>
              <textarea id="f1_h_condition_desc" rows={2} {...register('f1_h_condition_desc')} className="input-field" />
            </Field>
          )}
          <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 px-4 py-2.5">
            <label htmlFor="f1_h_checkup" className="text-gray-700 text-sm">
              {t('apply.f1.hCheckup', 'Bersedia mengikuti pemeriksaan kesehatan apabila dipersyaratkan perusahaan?')}
            </label>
            <div className="w-28 shrink-0"><YN id="f1_h_checkup" register={register} /></div>
          </div>
        </div>
      </FormSection>

      {/* == Kontak darurat ====================================== */}
      <FormSection
        index={11}
        icon="phoneRing"
        title={t('apply.f1.emergency', 'Kontak Darurat')}
      >
        <div className={grid2}>
          <Field id="f1_ec_name" label={t('apply.f1.ecName', 'Nama')}>
            <input id="f1_ec_name" type="text" {...register('f1_ec_name')} className="input-field" />
          </Field>
          <Field id="f1_ec_relation" label={t('apply.f1.ecRelation', 'Hubungan')}>
            <input id="f1_ec_relation" type="text" {...register('f1_ec_relation')} className="input-field" />
          </Field>
          <Field id="f1_ec_phone" label={t('apply.f1.ecPhone', 'No. HP')}>
            <input id="f1_ec_phone" type="tel" {...register('f1_ec_phone')} className="input-field" />
          </Field>
          <Field id="f1_ec_address" label={t('apply.f1.ecAddress', 'Alamat')}>
            <input id="f1_ec_address" type="text" {...register('f1_ec_address')} className="input-field" />
          </Field>
        </div>
      </FormSection>

      {/* == Pernyataan ========================================== */}
      <div className="rounded-xl border border-primary-200 bg-primary-50/50 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-2">{t('apply.f1.declaration', 'Pernyataan Calon')}</h3>
        <p className="text-sm text-gray-600 mb-3">
          {t('apply.f1.declarationText', 'Saya menyatakan bahwa seluruh data yang saya berikan adalah benar. Saya memahami bahwa WN Ampera memiliki budaya kerja berdasarkan nilai-nilai Islam dan Budaya SANTRI yang menjadi landasan tata perilaku kerja. Saya bersedia mematuhi ketentuan, SOP dan tata tertib WN Ampera apabila diterima bekerja.')}
        </p>
        <div className="flex items-start gap-3">
          <input type="checkbox" id="f1_declaration"
            {...register('f1_declaration', { required: t('apply.f1.declarationRequired', 'Centang pernyataan untuk melanjutkan') })}
            className="mt-0.5 w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500" />
          <label htmlFor="f1_declaration" className="text-sm text-gray-800 font-medium">
            {t('apply.f1.declarationAgree', 'Saya setuju dan menyatakan data di atas benar')} *
          </label>
        </div>
        {errors.f1_declaration && <p className="mt-1 text-sm text-red-600">{errors.f1_declaration.message}</p>}
      </div>
    </>
  );
}
